import { forwardRef } from "react";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const ease: [number, number, number, number] = [0.22, 1, 0.36, 1];

export const faqs = [
  {
    question: "What is UnlockEd Tutoring?",
    answer:
      "UnlockEd Tutoring is an AI-powered study platform built in Tasmania, for Tasmanian students in Years 10–12. It's aligned to the TASC curriculum for Level 2, 3, and 4 courses, so everything you practise maps directly to your TCE subjects.",
  },
  {
    question: "Is UnlockEd Tutoring only for Tasmanian students?",
    answer:
      "It's designed specifically for students studying TASC courses as part of the Tasmanian Certificate of Education (TCE). All curriculum content, learning outcomes, and assessment standards are aligned to TASC. Students outside Tasmania may still find it useful for general study skills, but the course content is Tasmanian-specific.",
  },
  {
    question: "How does the AI tutor work?",
    answer:
      "Our AI tutor, Dev, follows a \"hints, not handouts\" approach. Instead of giving you the answer, Dev provides progressive hints that guide you toward understanding — building genuine skills you can take into exams.",
  },
  {
    question: "Do I need to be in Hobart or Launceston to use UnlockEd?",
    answer:
      "No. UnlockEd is fully online, so it works the same whether you're in Hobart, Launceston, Devonport, Burnie, the East Coast or a small town on the West Coast. There's no travel, no scheduling around a tutor's availability, and no city-versus-regional gap in what you get — every student works through the same TASC-aligned content.",
  },
  {
    question: "Which subjects are supported?",
    answer:
      "We support a curated selection of TCE subjects aligned to the official TASC curriculum — not every TASC course. Browse our available subjects during setup or from Courses and Settings. If your subject isn’t listed, choose ‘Request a subject’ to tell us what you’re studying. We’ll review requests, but can’t guarantee availability.",
  },
  {
    question: "Is it free to use?",
    answer:
      "Yes — and until 30 November 2026 everything is free for every account: 3,000 AI credits a month, no card needed. From 1 December 2026 the free plan gives you 1 course and 150 AI credits per month (no credit card required), and paid plans are $12.99/month (Student) or $24.99/month (Family), each with a 14-day free trial. You can cancel any time from your billing settings.",
  },
  {
    question: "What are credits?",
    answer:
      "Credits power AI features like tutoring chat, question generation, and grading. Your allowance resets every month — on paid plans, at the start of each billing period (the date you subscribed). From 1 December 2026, paid plans can buy one-off top-ups: 100 credits for $4.99, 250 for $9.99 or 500 for $16.99.",
  },
  {
    question: "What happens when I cancel?",
    answer:
      "You can cancel at any time from your billing settings. When you cancel, your access continues until the end of your current paid billing period — you won't be charged again after that. Your data and progress are kept so you can pick up where you left off if you return.",
  },
  {
    question: "Can parents see my progress?",
    answer:
      "Yes — students generate a short-lived link code that a parent enters to connect. Parents can view progress and weekly reports, and can manage learning settings such as learning modes, learning windows and quiz schedules. They can't answer questions or change the student's work. They never receive the student's password.",
  },
  {
    question: "Is my data safe?",
    answer:
      "We handle personal information in line with the Australian Privacy Principles. You can request an export of your data (once every 24 hours) or delete your account from your in-app privacy settings. Some limited records may be kept where the law requires — see our Privacy Policy for details.",
  },
  {
    question: "What if the AI gives a wrong answer?",
    answer:
      "AI outputs are educational support only and may occasionally be inaccurate. Always use your own judgement, verify important information, and report anything that looks wrong via the support link.",
  },
];

const FAQSection = forwardRef<HTMLElement>((_, ref) => {
  const sectionRef = useRef<HTMLElement>(null);
  const isInView = useInView(sectionRef, { once: false, margin: "-100px" });

  return (
    <section
      ref={(node) => {
        (sectionRef as React.MutableRefObject<HTMLElement | null>).current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
      }}
      className="py-20 md:py-28"
    >
      <div className="container mx-auto px-6 md:px-8 lg:px-12 max-w-3xl">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease }}
          className="text-sm md:text-base font-semibold tracking-[0.2em] uppercase text-accent mb-4 text-center"
        >
          Questions
        </motion.p>

        {/* Headline */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease, delay: 0.1 }}
          className="font-display text-3xl md:text-4xl font-bold tracking-tight text-foreground text-center mb-4"
        >
          Frequently asked questions
        </motion.h2>

        {/* Gold divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
          transition={{ duration: 0.56, ease, delay: 0.2 }}
          className="h-[2px] w-16 mx-auto mb-10 bg-gradient-to-r from-accent to-transparent origin-center"
        />

        {/* Accordion */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, ease, delay: 0.3 }}
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-border/60">
                <AccordionTrigger className="text-left text-base md:text-lg font-medium text-foreground hover:no-underline hover:text-accent transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm md:text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </motion.div>

        {/* Support prompt */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.6, ease, delay: 0.5 }}
          className="text-center text-sm text-muted-foreground mt-8"
        >
          Still have questions?{" "}
          <a
            href="mailto:support@unlockedtutoring.com.au"
            className="text-accent underline underline-offset-2 hover:text-accent/80 transition-colors"
          >
            Get in touch
          </a>
        </motion.p>
      </div>
    </section>
  );
});

FAQSection.displayName = "FAQSection";

export default FAQSection;
